import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated, logout } from '../utils/auth';
import PageHeader from './PageHeader';
import './Leaderboard.css';

const fetchOptions = { credentials: 'include', headers: { 'Content-Type': 'application/json' } };

const Leaderboard = () => {
  const [groups, setGroups] = useState([]);
  const [groupLeaderboards, setGroupLeaderboards] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const navigate = useNavigate();

  const handleUnauthorized = useCallback(() => {
    fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
    logout();
    navigate('/login');
  }, [navigate]);

  const fetchGroupLeaderboards = useCallback(async (groupsData, year) => {
    const yearParam = year ? `?year=${year}` : '';
    const leaderboardEntries = await Promise.all(
      groupsData.map(async group => {
        const res = await fetch(`/api/groups/${group._id}/leaderboard${yearParam}`, fetchOptions);
        if (res.status === 401) throw new Error('UNAUTHORIZED');
        if (!res.ok) return [group._id, []];
        const data = await res.json();
        return [group._id, data];
      })
    );
    return Object.fromEntries(leaderboardEntries);
  }, []);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      try {
        const [meRes, groupsRes, yearsRes] = await Promise.all([
          fetch('/api/auth/me', fetchOptions),
          fetch('/api/groups', fetchOptions),
          fetch('/api/races/years', fetchOptions),
        ]);

        if (meRes.status === 401 || groupsRes.status === 401) {
          handleUnauthorized();
          return;
        }

        if (!meRes.ok) {
          setError('Failed to load user data.');
          return;
        }

        const meData = await meRes.json();
        setCurrentUser(meData);

        if (!groupsRes.ok) {
          setError('Failed to load groups.');
          return;
        }

        const groupsData = await groupsRes.json();
        if (Array.isArray(groupsData)) {
          setGroups(groupsData);
        } else {
          console.error('Unexpected response from /api/groups:', groupsData);
          setGroups([]);
        }

        let defaultYear = null;
        if (yearsRes.ok) {
          const years = await yearsRes.json();
          setAvailableYears(years);
          defaultYear = years.length > 0 ? years[0] : null;
          setSelectedYear(defaultYear);
        }

        const leaderboards = await fetchGroupLeaderboards(groupsData, defaultYear);
        setGroupLeaderboards(leaderboards);
      } catch (err) {
        if (err.message === 'UNAUTHORIZED') {
          handleUnauthorized();
          return;
        }
        setError('Failed to load standings.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate, handleUnauthorized, fetchGroupLeaderboards]);

  useEffect(() => {
    if (groups.length > 0 && selectedYear !== undefined) {
      fetchGroupLeaderboards(groups, selectedYear).then(setGroupLeaderboards).catch(err => {
        if (err.message === 'UNAUTHORIZED') handleUnauthorized();
      });
    }
  }, [selectedYear, groups, fetchGroupLeaderboards, handleUnauthorized]);

  if (loading) {
    return <div className="leaderboard"><p>Loading standings...</p></div>;
  }

  if (error) {
    return <div className="leaderboard"><p className="lb-error">{error}</p></div>;
  }

  return (
    <div className="leaderboard">
      <div className="lb-header">
        <PageHeader overline="My Groups" title="GROUP STANDINGS" />
        <button onClick={() => navigate('/dashboard')} className="pick-button" style={{ maxWidth: 180 }}>
          ← Back to Dashboard
        </button>
      </div>

      {availableYears.length > 0 && (
        <div className="season-selector">
          <span className="season-label">Season:</span>
          {availableYears.map(year => (
            <button
              key={year}
              className={`season-btn${selectedYear === year ? ' season-btn--active' : ''}`}
              onClick={() => setSelectedYear(year)}
            >
              {year}
            </button>
          ))}
          <button
            className={`season-btn${selectedYear === null ? ' season-btn--active' : ''}`}
            onClick={() => setSelectedYear(null)}
          >
            All
          </button>
        </div>
      )}

      {groups.length === 0 ? (
        <div className="lb-no-groups">
          <p className="lb-empty">
            You are not in any groups yet.
          </p>
          {currentUser && (
            <p className="lb-points-solo">Your total points: <strong>{currentUser.totalPoints ?? 0}</strong></p>
          )}
          <p>
            <button onClick={() => navigate('/groups')} className="pick-button" style={{ maxWidth: 220 }}>
              Join or Create a Group
            </button>
          </p>
        </div>
      ) : (
        groups.map(group => (
          <div key={group._id} className="lb-group-section">
            <h2 className="lb-group-name">{group.name}</h2>
            {!groupLeaderboards[group._id] || groupLeaderboards[group._id].length === 0 ? (
              <p className="lb-empty">No players yet.</p>
            ) : (
              <div className="lb-table-wrapper">
                <table className="lb-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Player</th>
                      <th>Points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupLeaderboards[group._id].map(entry => {
                      const isCurrentUser = currentUser && currentUser.name === entry.name;
                      return (
                        <tr key={entry.name} className={isCurrentUser ? 'lb-row lb-row-current' : 'lb-row'}>
                          <td className="lb-rank">
                            {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : `#${entry.rank}`}
                          </td>
                          <td className="lb-player">
                            {entry.avatar && (
                              <img src={entry.avatar} alt={entry.name} className="lb-avatar" referrerPolicy="no-referrer" />
                            )}
                            <span>{entry.name}</span>
                          </td>
                          <td className="lb-points">{entry.totalPoints}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
};

export default Leaderboard;
